import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Briefcase, Building2, Target, Sparkles, GraduationCap, Wrench, MapPin, ArrowRight, ArrowLeft, Check, X, Upload, FileText, Loader2 } from "lucide-react";

import { useI18n } from "@/i18n";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Command, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, track } from "@/lib/api";

const SAP_LEVELS = [
  { v: "none", de: "Keine", en: "None", d_de: "Ich habe noch nicht mit SAP gearbeitet", d_en: "I have not worked with SAP yet" },
  { v: "basic", de: "Grundlagen", en: "Basic", d_de: "Klick-Erfahrung, keine Vertiefung", d_en: "Click-through only" },
  { v: "user", de: "Anwender:in", en: "User", d_de: "Tägliche Arbeit im SAP-System", d_en: "Daily use of SAP" },
  { v: "advanced", de: "Fortgeschritten", en: "Advanced", d_de: "Key User oder tiefes Prozesswissen", d_en: "Key User or deep process knowledge" },
  { v: "consultant", de: "Consultant", en: "Consultant", d_de: "Customizing / Beratung", d_en: "Customizing / Consulting" },
];

const GOALS_DE = [
  { v: "grow_in_role", label: "In meiner aktuellen Rolle wachsen" },
  { v: "key_user", label: "Key User werden" },
  { v: "inhouse", label: "SAP Inhouse Consultant werden" },
  { v: "external", label: "SAP Berater (Beratungshaus) werden" },
  { v: "freelance", label: "SAP Freelancer werden" },
  { v: "explore", label: "Ich möchte Optionen erkunden" },
];
const GOALS_EN = [
  { v: "grow_in_role", label: "Grow in my current role" },
  { v: "key_user", label: "Become a Key User" },
  { v: "inhouse", label: "Become an SAP Inhouse Consultant" },
  { v: "external", label: "Become an external SAP Consultant" },
  { v: "freelance", label: "Become an SAP Freelancer" },
  { v: "explore", label: "Explore my options" },
];

const EDU = ["Ausbildung / Vocational", "Bachelor", "Master", "Promotion / PhD", "Andere / Other"];

const STEPS = ["method", "input", "profession", "context", "tasks", "sap", "goal", "review", "followup", "email"];

export default function Assessment() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState("method");
  const [sid, setSid] = useState(null);
  const [loading, setLoading] = useState(false);

  // Structured profile fields
  const [firstName, setFirstName] = useState("");
  const [profession, setProfession] = useState(null);       // {id, name_de, name_en, family, career_path, ...}
  const [customTitle, setCustomTitle] = useState("");
  const [industry, setIndustry] = useState("");
  const [education, setEducation] = useState("");
  const [years, setYears] = useState(3);
  const [location, setLocation] = useState("");
  const [tasks, setTasks] = useState([]);                    // string[]
  const [customTask, setCustomTask] = useState("");
  const [sapLevel, setSapLevel] = useState("");
  const [skills, setSkills] = useState([]);                  // string[]
  const [goal, setGoal] = useState("");
  const [languages, setLanguages] = useState(["Deutsch"]);
  const [prefilled, setPrefilled] = useState(new Set());

  // Data
  const [professions, setProfessions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [suggestedTasks, setSuggestedTasks] = useState([]);
  const [suggestedSkills, setSuggestedSkills] = useState([]);

  // Follow-up
  const [followups, setFollowups] = useState([]);
  const [answers, setAnswers] = useState({});
  const [match, setMatch] = useState(null);
  const [email, setEmail] = useState("");

  // Load taxonomies once
  useEffect(() => {
    api.get("/taxonomy/professions").then(r => setProfessions(r.data)).catch(() => {});
    api.get("/taxonomy/industries").then(r => setIndustries(r.data)).catch(() => {});
  }, []);

  // When profession changes, refresh dynamic task & skill lists
  useEffect(() => {
    if (!profession?.id) { setSuggestedTasks([]); setSuggestedSkills([]); return; }
    api.get(`/taxonomy/tasks?profession_id=${profession.id}`).then(r => setSuggestedTasks(r.data || [])).catch(() => {});
    const cpid = profession.primary_career_path_id || "";
    api.get(`/taxonomy/skills?career_path_id=${encodeURIComponent(cpid)}`).then(r => setSuggestedSkills(r.data || [])).catch(() => {});
  }, [profession?.id]);

  const stepIndex = STEPS.indexOf(step);
  const progress = Math.round(((stepIndex + 1) / STEPS.length) * 100);

  async function startSession(method) {
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/start", { lang, method });
      setSid(data.session_id);
      setStep(method === "cv" ? "input" : "profession");
    } finally { setLoading(false); }
  }

  async function uploadCV(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lang", lang);
    try {
      const { data } = await api.post(`/assessment/${sid}/cv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = data.profile || {};
      // Pre-fill structured pickers
      if (p.first_name) setFirstName(p.first_name);
      setCustomTitle(p.current_position || "");
      setIndustry(p.industry || "");
      setEducation(p.education_normalized || p.education || "");
      setYears(p.years_experience || 3);
      setLocation(p.location || "");
      setTasks(p.professional_tasks || []);
      setSapLevel(p.sap_experience || "");
      setSkills([...(p.transferable_skills || []), ...(p.software || []), ...(p.erp_systems || [])].filter(Boolean));
      setLanguages(p.languages && p.languages.length ? p.languages : ["Deutsch"]);
      // Track which fields were prefilled from CV (used for "aus deinem Lebenslauf übernommen" hint)
      setPrefilled(new Set([
        p.first_name && "first_name",
        p.current_position && "profession",
        p.industry && "industry",
        (p.education_normalized || p.education) && "education",
        p.years_experience && "years",
        p.location && "location",
        (p.professional_tasks || []).length && "tasks",
        ((p.transferable_skills || []).length || (p.software || []).length) && "skills",
        p.sap_experience && p.sap_experience !== "none" && "sap",
      ].filter(Boolean)));
      // try to match profession by extracted current_position
      const guess = findProfessionByTitle(p.current_position, professions);
      if (guess) setProfession(guess);
      setStep("profession");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setLoading(false); }
  }

  function findProfessionByTitle(title, list) {
    if (!title) return null;
    const t = title.toLowerCase();
    return list.find(p =>
      (p.name_de && t.includes(p.name_de.toLowerCase().split(" ")[0])) ||
      (p.name_en && t.includes(p.name_en.toLowerCase().split(" ")[0])) ||
      (p.aliases || []).some(a => t.includes(a.toLowerCase().split(" ")[0]))
    );
  }

  async function submitStructured() {
    // Build the questionnaire payload
    const payload = {
      first_name: firstName.trim(),
      current_position: profession ? (lang === "de" ? profession.name_de : profession.name_en) : customTitle,
      industry, years_experience: Number(years) || 0, education,
      professional_tasks: tasks, skills,
      software: [], erp_systems: [], sap_experience: sapLevel || "none",
      career_goal: goal, learning_hours: 5, location,
      work_preferences: [], languages, transferable_skills: skills,
    };
    setLoading(true);
    try {
      await api.post(`/assessment/${sid}/questionnaire`, payload);
      await api.post(`/assessment/${sid}/confirm`, payload);
      const { data } = await api.post(`/assessment/${sid}/followup`);
      setFollowups(data.questions || []);
      if ((data.questions || []).length > 0) setStep("followup");
      else await runMatch();
    } finally { setLoading(false); }
  }

  async function submitFollowups() {
    setLoading(true);
    try {
      const qas = followups.map((q, i) => ({ q, a: answers[i] || "" }));
      await api.post(`/assessment/${sid}/followup-answers`, { qas });
      await runMatch();
    } finally { setLoading(false); }
  }

  async function runMatch() {
    setLoading(true);
    try {
      const { data } = await api.post(`/assessment/${sid}/match`);
      setMatch(data);
      setStep("email");
    } finally { setLoading(false); }
  }

  const [marketingOptIn, setMarketingOptIn] = useState(false);

  async function submitEmail(e) {
    e?.preventDefault?.();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      // pull UTM + referrer from window
      const params = new URLSearchParams(window.location.search);
      const utm = {
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        referrer: document.referrer || null,
      };
      const { data } = await api.post(`/assessment/${sid}/email`, { email, consent: true, marketing_opt_in: marketingOptIn, ...utm });
      toast.success(data.email_sent ? "Report sent" : "Report ready");
      track("assessment_completed", sid);
      nav(`/report/${data.report_token}`);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }

  const canGoNext = {
    profession: !!(profession || customTitle.trim().length >= 3),
    context: !!(industry && education && years >= 0),
    tasks: tasks.length >= 1,
    sap: !!sapLevel,
    goal: !!goal,
    review: true,
  }[step] ?? true;

  return (
    <div className="min-h-screen">
      <Brand />
      <Toaster richColors position="top-center" />

      <div className="sticky top-[65px] z-30 border-b border-border/60 glass">
        <div className="mx-auto max-w-3xl px-6 py-3 flex items-center gap-4">
          <div className="flex-1">
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <motion.div className="h-full bg-primary" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.35 }}/>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground shrink-0">
            {stepIndex + 1} / {STEPS.length}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-6 py-10 md:py-14">
        <AnimatePresence mode="wait">
          <motion.div key={step}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}>

            {step === "method" && (
              <MethodStep
                onCV={() => startSession("cv")}
                onQ={() => startSession("questionnaire")}
                loading={loading}
                t={t}
              />
            )}

            {step === "input" && (
              <CVUpload sid={sid} lang={lang} onDone={uploadCV} loading={loading} t={t} onSkip={() => setStep("profession")} />
            )}

            {step === "profession" && (
              <ProfessionStep
                lang={lang}
                professions={professions}
                profession={profession} setProfession={setProfession}
                customTitle={customTitle} setCustomTitle={setCustomTitle}
                firstName={firstName} setFirstName={setFirstName}
                prefilled={prefilled}
              />
            )}

            {step === "context" && (
              <ContextStep
                industries={industries}
                industry={industry} setIndustry={setIndustry}
                education={education} setEducation={setEducation}
                years={years} setYears={setYears}
                location={location} setLocation={setLocation}
                lang={lang}
                prefilled={prefilled}
              />
            )}

            {step === "tasks" && (
              <TasksStep
                suggestedTasks={suggestedTasks}
                tasks={tasks} setTasks={setTasks}
                customTask={customTask} setCustomTask={setCustomTask}
                suggestedSkills={suggestedSkills}
                skills={skills} setSkills={setSkills}
                lang={lang}
                prefilled={prefilled}
              />
            )}

            {step === "sap" && (
              <SapStep sapLevel={sapLevel} setSapLevel={setSapLevel} lang={lang} prefilled={prefilled} />
            )}

            {step === "goal" && (
              <GoalStep goal={goal} setGoal={setGoal} lang={lang} />
            )}

            {step === "review" && (
              <ReviewStep
                lang={lang}
                profession={profession} customTitle={customTitle}
                industry={industry} years={years} education={education} location={location}
                tasks={tasks} skills={skills} sapLevel={sapLevel} goal={goal}
              />
            )}

            {step === "followup" && (
              <FollowupStep questions={followups} answers={answers} setAnswers={setAnswers} lang={lang} />
            )}

            {step === "email" && (
              <EmailStep match={match} email={email} setEmail={setEmail} onSubmit={submitEmail} loading={loading} lang={lang}
                         marketingOptIn={marketingOptIn} setMarketingOptIn={setMarketingOptIn} />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        {!["method", "input", "email"].includes(step) && (
          <div className="mt-10 flex items-center justify-between">
            <Button variant="ghost" data-testid="nav-back" onClick={() => {
              const i = STEPS.indexOf(step);
              if (i > 0) setStep(STEPS[Math.max(0, i - 1)]);
            }} disabled={loading}>
              <ArrowLeft className="h-4 w-4 mr-2"/> {t("back")}
            </Button>
            {step === "review" ? (
              <Button size="lg" data-testid="nav-run-match" onClick={submitStructured} disabled={loading}>
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>{t("processing")}</> : <>{lang === "de" ? "Match starten" : "Run match"} <Sparkles className="h-4 w-4 ml-2"/></>}
              </Button>
            ) : step === "followup" ? (
              <Button size="lg" data-testid="followup-submit" onClick={submitFollowups} disabled={loading}>
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>{t("processing")}</> : <>{t("next")} <ArrowRight className="h-4 w-4 ml-2"/></>}
              </Button>
            ) : (
              <Button size="lg" data-testid="nav-next" onClick={() => {
                const i = STEPS.indexOf(step);
                setStep(STEPS[Math.min(STEPS.length - 1, i + 1)]);
              }} disabled={!canGoNext || loading}>
                {t("next")} <ArrowRight className="h-4 w-4 ml-2"/>
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

/* ---------- Step components ---------- */

function MethodStep({ onCV, onQ, loading, t }) {
  return (
    <div>
      <StepHeader title={t("method_title")} icon={Sparkles} />
      <div className="mt-8 grid gap-4">
        <MethodCard testid="method-cv" title={t("method_cv")} desc={t("method_cv_d")} icon={FileText} onClick={onCV} disabled={loading}/>
        <MethodCard testid="method-questionnaire" title={t("method_q")} desc={t("method_q_d")} icon={Briefcase} onClick={onQ} disabled={loading}/>
      </div>
    </div>
  );
}

function MethodCard({ testid, title, desc, icon: Icon, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="text-left border border-border rounded-2xl p-6 bg-card hover:border-primary/60 hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex items-start gap-4">
      <div className="h-12 w-12 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center">
        <Icon className="h-6 w-6"/>
      </div>
      <div>
        <div className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{desc}</div>
      </div>
      <ArrowRight className="ml-auto h-5 w-5 self-center text-muted-foreground"/>
    </button>
  );
}

function CVUpload({ sid, lang, onDone, loading, t, onSkip }) {
  const inputRef = React.useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div>
      <StepHeader title={t("upload_hint")} icon={Upload} subtitle={t("upload_types")} />
      <input ref={inputRef} type="file" accept=".pdf,.docx" className="hidden"
             data-testid="cv-file-input" onChange={(e) => onDone(e.target.files?.[0])} />
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onDone(e.dataTransfer.files?.[0]); }}
        className={`mt-8 border-2 border-dashed rounded-2xl p-14 cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
        <div className="grid place-items-center gap-3">
          <div className="h-16 w-16 rounded-full bg-primary/10 text-primary grid place-items-center">
            {loading ? <Loader2 className="h-8 w-8 animate-spin"/> : <Upload className="h-8 w-8"/>}
          </div>
          <div className="text-lg font-semibold">{loading ? t("processing") : t("upload_hint")}</div>
          <div className="text-xs text-muted-foreground">{t("upload_types")}</div>
        </div>
      </div>
      <div className="mt-6 text-center">
        <button onClick={onSkip} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4" data-testid="cv-skip">
          {lang === "de" ? "Ohne CV fortfahren →" : "Continue without CV →"}
        </button>
      </div>
      {/* v2.15 — passive privacy line (no consent checkbox, no blocking) */}
      <div className="mt-4 text-center text-[11px] text-muted-foreground" data-testid="cv-privacy-note">
        {lang === "de"
          ? <>Hinweise zur Verarbeitung deines Lebenslaufs findest du in unserer <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">Datenschutzerklärung</a>.</>
          : <>Details on how we process your CV are described in our <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">privacy policy</a>.</>}
      </div>
    </div>
  );
}

function StepHeader({ title, subtitle, icon: Icon }) {
  return (
    <div>
      <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3 text-accent"/>}
        <span>Step</span>
      </div>
      <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

function PrefilledHint({ show, t }) {
  if (!show) return null;
  return (
    <div className="mt-1 text-[10px] text-accent inline-flex items-center gap-1" data-testid="prefilled-hint">
      <Sparkles className="h-3 w-3"/> {t("prefilled_hint")}
    </div>
  );
}

function ProfessionStep({ lang, professions, profession, setProfession, customTitle, setCustomTitle, firstName, setFirstName, prefilled }) {
  const [open, setOpen] = useState(false);
  const label = profession ? (lang === "de" ? profession.name_de : profession.name_en) : (customTitle || "");
  const { t } = useI18n();
  const isPre = (k) => prefilled && prefilled.has(k);
  return (
    <div>
      <StepHeader
        title={lang === "de" ? "Was ist deine aktuelle Rolle?" : "What's your current role?"}
        subtitle={lang === "de" ? "Wähle den nächstpassenden Beruf aus unserer Datenbank. Nicht dabei? Trage einen eigenen Titel ein." : "Pick the closest match from our database, or type your own."}
        icon={Briefcase}
      />
      <div className="mt-8 space-y-6">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("name_question")}</Label>
          <Input data-testid="first-name" className="mt-2" placeholder={t("name_placeholder")}
                 value={firstName || ""} onChange={e => setFirstName(e.target.value)} />
          <PrefilledHint show={isPre("first_name")} t={t}/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Beruf" : "Profession"}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button data-testid="profession-picker"
              className="mt-2 w-full h-12 rounded-lg border border-border bg-card px-4 text-left flex items-center justify-between hover:border-primary/60 transition-colors">
              <span className={label ? "" : "text-muted-foreground"}>{label || (lang === "de" ? "Beruf suchen…" : "Search profession…")}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90"/>
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
            <Command>
              <CommandInput placeholder={lang === "de" ? "Beruf suchen…" : "Search profession…"} data-testid="profession-search"/>
              <CommandList className="max-h-72">
                <CommandEmpty>{lang === "de" ? "Kein Treffer" : "No match"}</CommandEmpty>
                {Array.from(new Set(professions.map(p => p.family))).map(fam => (
                  <CommandGroup key={fam} heading={fam}>
                    {professions.filter(p => p.family === fam).map(p => (
                      <CommandItem key={p.id} value={`${p.name_de} ${p.name_en} ${(p.aliases || []).join(" ")}`}
                        onSelect={() => { setProfession(p); setCustomTitle(""); setOpen(false); }}
                        data-testid={`profession-opt-${p.id}`}>
                        <div className="flex-1">
                          <div className="font-medium">{lang === "de" ? p.name_de : p.name_en}</div>
                          <div className="text-xs text-muted-foreground">{p.career_path}{p.specialization ? ` · ${p.specialization}` : ""}</div>
                        </div>
                        {profession?.id === p.id && <Check className="h-4 w-4 text-primary"/>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {profession && (
          <div className="mt-3 rounded-lg border border-accent/40 bg-accent/5 px-4 py-3 text-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-accent">Detected career path</div>
            <div className="mt-1 font-semibold">{profession.career_path}{profession.specialization ? ` · ${profession.specialization}` : ""}</div>
          </div>
        )}
        <div className="mt-6 text-xs text-muted-foreground">— {lang === "de" ? "oder" : "or"} —</div>
        <Input data-testid="custom-title" value={customTitle} onChange={e => { setCustomTitle(e.target.value); setProfession(null); }} placeholder={lang === "de" ? "Eigener Titel (z. B. Junior Controller)" : "Custom title (e.g. Junior Controller)"} className="mt-2"/>
        <PrefilledHint show={isPre("profession")} t={t}/>
        </div>
      </div>
    </div>
  );
}

function ContextStep({ industries, industry, setIndustry, education, setEducation, years, setYears, location, setLocation, lang, prefilled }) {
  const { t } = useI18n();
  const isPre = (k) => prefilled && prefilled.has(k);
  return (
    <div>
      <StepHeader title={lang === "de" ? "Dein Kontext" : "Your context"} icon={Building2}
        subtitle={lang === "de" ? "Ein paar Angaben zur Branche und Erfahrung." : "A few details about your industry and experience."}/>
      <div className="mt-8 space-y-6">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Branche" : "Industry"}</Label>
          <ComboSelect data-testid="industry" value={industry} onChange={setIndustry} options={industries}
                       placeholder={lang === "de" ? "Branche wählen…" : "Choose industry…"}/>
          <PrefilledHint show={isPre("industry")} t={t}/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Höchste Ausbildung" : "Highest education"}</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {EDU.map(o => (
              <Chip key={o} testid={`edu-${o}`} active={education === o} onClick={() => setEducation(o)}>{o}</Chip>
            ))}
          </div>
          <PrefilledHint show={isPre("education")} t={t}/>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Berufsjahre" : "Years of experience"}</Label>
            <span className="text-lg font-semibold" data-testid="years-value">{years}+</span>
          </div>
          <input data-testid="years-slider" type="range" min="0" max="30" step="1" value={years} onChange={e => setYears(parseInt(e.target.value))}
            className="mt-3 w-full accent-primary"/>
          <div className="flex justify-between text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
            <span>0</span><span>10</span><span>20</span><span>30+</span>
          </div>
          <PrefilledHint show={isPre("years")} t={t}/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Standort" : "Location"}</Label>
          <div className="mt-2 relative">
            <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input data-testid="location" value={location} onChange={e => setLocation(e.target.value)} placeholder={lang === "de" ? "z. B. München" : "e.g. Munich"} className="pl-9"/>
          </div>
          <PrefilledHint show={isPre("location")} t={t}/>
        </div>
      </div>
    </div>
  );
}

function ComboSelect({ value, onChange, options, placeholder, "data-testid": testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid}
          className="mt-2 w-full h-11 rounded-lg border border-border bg-card px-4 text-left flex items-center justify-between hover:border-primary/60 transition-colors">
          <span className={value ? "" : "text-muted-foreground"}>{value || placeholder}</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90"/>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder={placeholder}/>
          <CommandList className="max-h-64">
            <CommandEmpty>—</CommandEmpty>
            <CommandGroup>
              {options.map(o => (
                <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); }}>
                  {o} {value === o && <Check className="ml-auto h-4 w-4 text-primary"/>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TasksStep({ suggestedTasks, tasks, setTasks, customTask, setCustomTask, suggestedSkills, skills, setSkills, lang, prefilled }) {
  const { t } = useI18n();
  const isPre = (k) => prefilled && prefilled.has(k);
  const toggle = (arr, setArr, v) => arr.includes(v) ? setArr(arr.filter(x => x !== v)) : setArr([...arr, v]);
  const CUSTOM_TASK_MAX = 80;
  const inputRef = useRef(null);
  const addCustomTask = () => {
    const raw = customTask.trim().slice(0, CUSTOM_TASK_MAX);
    if (!raw) { setCustomTask(""); if (inputRef.current) inputRef.current.focus(); return; }
    const lower = raw.toLowerCase();
    const existingMatch = tasks.find(x => x.toLowerCase() === lower);
    const suggestedMatch = suggestedTasks.find(x => x.toLowerCase() === lower);
    if (existingMatch) {
      // duplicate — do not add again, but clear + focus for continued entry
    } else if (suggestedMatch) {
      // use canonical DB form
      setTasks([...tasks, suggestedMatch]);
    } else {
      setTasks([...tasks, raw]);
    }
    setCustomTask("");
    if (inputRef.current) inputRef.current.focus();
  };
  const customChips = useMemo(
    () => tasks.filter(t => !suggestedTasks.some(s => s.toLowerCase() === t.toLowerCase())),
    [tasks, suggestedTasks]
  );
  const skillGroups = useMemo(() => {
    const g = {};
    for (const s of suggestedSkills) {
      const k = s.category || "General";
      (g[k] = g[k] || []).push(s.canonical_skill);
    }
    return g;
  }, [suggestedSkills]);
  return (
    <div>
      <StepHeader title={lang === "de" ? "Was machst du wirklich?" : "What do you actually do?"} icon={Wrench}
        subtitle={lang === "de" ? "Tippe an, was auf dich zutrifft. Alles hier stammt aus unserer Datenbank." : "Tap what applies to you. Everything here comes from our database."}/>
      <div className="mt-8">
        <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Typische Aufgaben" : "Typical tasks"}</Label>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="task-chips">
          {suggestedTasks.length === 0 && customChips.length === 0 && (
            <div className="text-xs text-muted-foreground">
              {lang === "de" ? "Wähle zuerst deinen Beruf, um Aufgaben zu sehen." : "Pick a profession first to see tasks."}
            </div>
          )}
          {suggestedTasks.map(t => (
            <Chip key={t} testid={`task-${t.slice(0, 20)}`} active={tasks.includes(t)} onClick={() => toggle(tasks, setTasks, t)}>{t}</Chip>
          ))}
          {customChips.map(t => (
            <Chip key={`custom:${t}`} testid={`task-custom-${t.slice(0, 20)}`} active onClick={() => setTasks(tasks.filter(x => x !== t))}>{t}</Chip>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Input
            ref={inputRef}
            data-testid="custom-task"
            maxLength={CUSTOM_TASK_MAX}
            placeholder={lang === "de" ? "Andere Aufgabe hinzufügen…" : "Add another task…"}
            value={customTask}
            onChange={e => setCustomTask(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                addCustomTask();
              }
            }}
          />
          <Button type="button" variant="outline" data-testid="add-task" onClick={addCustomTask}>+</Button>
        </div>
        <PrefilledHint show={isPre("tasks")} t={t}/>
      </div>

      {Object.keys(skillGroups).length > 0 && (
        <div className="mt-10">
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lang === "de" ? "Kenntnisse & Skills" : "Skills"}</Label>
          <div className="mt-3 space-y-4">
            {Object.entries(skillGroups).map(([cat, items]) => (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{cat}</div>
                <div className="flex flex-wrap gap-2">
                  {items.map(s => (
                    <Chip key={s} testid={`skill-${s.slice(0, 15)}`} active={skills.includes(s)} onClick={() => toggle(skills, setSkills, s)}>{s}</Chip>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <PrefilledHint show={isPre("skills")} t={t}/>
        </div>
      )}
    </div>
  );
}

function SapStep({ sapLevel, setSapLevel, lang, prefilled }) {
  const { t } = useI18n();
  const isPre = (k) => prefilled && prefilled.has(k);
  return (
    <div>
      <StepHeader title={lang === "de" ? "Wie viel SAP hast du bereits gemacht?" : "How much SAP have you already done?"} icon={GraduationCap}/>
      <div className="mt-8 grid gap-3">
        {SAP_LEVELS.map(l => (
          <button key={l.v} data-testid={`sap-${l.v}`}
            onClick={() => setSapLevel(l.v)}
            className={`text-left border rounded-xl p-5 transition-transform hover:-translate-y-0.5 ${sapLevel === l.v ? "border-primary bg-primary/5 ai-glow" : "border-border bg-card"}`}>
            <div className="flex items-center gap-3">
              <div className={`h-9 w-9 rounded-lg grid place-items-center ${sapLevel === l.v ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <GraduationCap className="h-5 w-5"/>
              </div>
              <div>
                <div className="font-semibold">{lang === "de" ? l.de : l.en}</div>
                <div className="text-xs text-muted-foreground">{lang === "de" ? l.d_de : l.d_en}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <PrefilledHint show={isPre("sap")} t={t}/>
    </div>
  );
}

function GoalStep({ goal, setGoal, lang }) {
  const opts = lang === "de" ? GOALS_DE : GOALS_EN;
  return (
    <div>
      <StepHeader title={lang === "de" ? "Wohin willst du?" : "Where do you want to go?"} icon={Target}/>
      <div className="mt-8 grid gap-3">
        {opts.map(o => (
          <button key={o.v} data-testid={`goal-${o.v}`}
            onClick={() => setGoal(o.v)}
            className={`text-left border rounded-xl p-4 transition-transform hover:-translate-y-0.5 flex items-center gap-3 ${goal === o.v ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
            <Target className={`h-5 w-5 ${goal === o.v ? "text-primary" : "text-muted-foreground"}`}/>
            <span className="font-medium">{o.label}</span>
            {goal === o.v && <Check className="ml-auto h-4 w-4 text-primary"/>}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewStep({ lang, profession, customTitle, industry, years, education, location, tasks, skills, sapLevel, goal }) {
  const t = lang === "de" ? {
    title: "Zusammenfassung", desc: "Passt alles? Dann berechnen wir dein Match.",
    role: "Rolle", ind: "Branche", edu: "Ausbildung", exp: "Erfahrung", loc: "Standort",
    tasks: "Aufgaben", skills: "Skills", sap: "SAP", goal: "Ziel"
  } : {
    title: "Summary", desc: "Everything correct? Then we'll calculate your match.",
    role: "Role", ind: "Industry", edu: "Education", exp: "Experience", loc: "Location",
    tasks: "Tasks", skills: "Skills", sap: "SAP", goal: "Goal"
  };
  const rows = [
    [t.role, profession ? (lang === "de" ? profession.name_de : profession.name_en) : customTitle],
    [t.ind, industry], [t.edu, education], [t.exp, `${years}+ ${lang === "de" ? "Jahre" : "years"}`], [t.loc, location || "—"],
    [t.sap, sapLevel], [t.goal, goal],
  ];
  return (
    <div>
      <StepHeader title={t.title} subtitle={t.desc} icon={Sparkles}/>
      <div className="mt-8 rounded-2xl border border-border bg-card divide-y divide-border">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 px-5 py-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
            <div className="text-sm font-medium text-right">{v || "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t.tasks} · {tasks.length}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {tasks.map(x => <span key={x} className="text-xs rounded-full border border-border px-3 py-1">{x}</span>)}
          {tasks.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </div>
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t.skills} · {skills.length}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {skills.map(x => <span key={x} className="text-xs rounded-full border border-border px-3 py-1">{x}</span>)}
          {skills.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </div>
    </div>
  );
}

function FollowupStep({ questions, answers, setAnswers, lang }) {
  return (
    <div>
      <StepHeader title={lang === "de" ? "Drei kurze Fragen" : "Three quick questions"}
        subtitle={lang === "de" ? "Damit wird das Match noch präziser." : "So the match becomes even more precise."}
        icon={Sparkles}/>
      <div className="mt-8 space-y-4">
        {questions.map((q, i) => (
          <div key={i} className="rounded-xl border border-border p-5 bg-card">
            <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-2">Question {i + 1}</div>
            <div className="font-medium">{q}</div>
            <Textarea data-testid={`followup-${i}`} className="mt-3" rows={2}
              value={answers[i] || ""} onChange={e => setAnswers(a => ({ ...a, [i]: e.target.value }))}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailStep({ match, email, setEmail, onSubmit, loading, lang, marketingOptIn, setMarketingOptIn }) {
  const top = match?.top_recommendation || {};
  const role = top.job_role || {};
  const tiles = match?.salary_tiles || [];
  const cert = match?.certification || {};
  const sa = match?.skill_analysis || {};
  const prep = match?.prep_courses || [];
  const roadmap = (match?.roadmap_8_weeks || {}).phases || [];
  const t = (k) => {
    const dict = {
      de: {
        head_ready: "Dein persönlicher SAP-Karriere-Report ist bereit",
        head_sub: "Wir haben deinen passenden Pfad, Gehaltsbandbreite, Zertifizierung und Lern-Roadmap deterministisch ermittelt.",
        unlock_title: "E-Mail eingeben – alles ansehen & Report per E-Mail erhalten",
        unlock_desc: "Damit du deinen kompletten Report öffnest und eine Kopie in dein Postfach bekommst, gib bitte deine E-Mail-Adresse ein.",
        placeholder: "du@firma.de",
        open: "Report freischalten",
        free_line: "100 % kostenlos starten — keine Zahlungsdaten nötig",
        salary_sec: "Deine Gehaltsentwicklung", roadmap_sec: "Lern-Roadmap",
        strong: "Bereits stark", partial: "Teilweise", gap: "Zu entwickeln",
        cert_sec: "Zertifizierung", prep_sec: "Deine Zertifizierungsvorbereitung",
        match_score: "Match-Score", confidence: "Konfidenz",
      },
      en: {
        head_ready: "Your personal SAP career report is ready",
        head_sub: "We've deterministically matched your path, salary bracket, certification and learning roadmap.",
        unlock_title: "Enter your email – see everything and get the report by email",
        unlock_desc: "To open the full report and receive a copy in your inbox, please enter your email address.",
        placeholder: "you@company.com",
        open: "Unlock report",
        free_line: "100% free — no credit card required",
        salary_sec: "Your salary progression", roadmap_sec: "Learning roadmap",
        strong: "Already strong", partial: "Partial", gap: "Needs development",
        cert_sec: "Certification", prep_sec: "Certification preparation",
        match_score: "Match score", confidence: "Confidence",
      },
    };
    return dict[lang]?.[k] ?? dict.en[k] ?? k;
  };

  return (
    <div className="relative -mx-6 md:-mx-0" data-testid="email-gate">
      {/* Sharp Header — visible above the blur */}
      <div className="relative z-20 px-6 md:px-0">
        <div className="text-[11px] uppercase tracking-[0.24em] text-accent">{lang === "de" ? "Dein Match ist bereit" : "Your match is ready"}</div>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="email-gate-title">
          {top.career_path_name}
        </h1>
        <div className="mt-1 text-sm text-muted-foreground">{role.title_de || role.title_en} · {top.level}</div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("head_sub")}</p>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3 max-w-xl">
          <MiniStat label={t("match_score")} value={`${top.match_score ?? 0}%`} testid="preview-score"/>
          <MiniStat label={t("confidence")} value={match?.confidence || "—"} testid="preview-confidence"/>
          <MiniStat label={lang === "de" ? "Zertifizierung" : "Certification"} value={cert.exam_code || cert.name?.slice(0, 12) || "—"} testid="preview-cert"/>
        </div>
      </div>

      {/* Blurred realistic preview + top-anchored email overlay */}
      <div className="relative mt-4 md:mt-6">
        <div aria-hidden="true"
             className="pointer-events-none select-none space-y-4 px-6 md:px-0"
             style={{ filter: "blur(9px)", WebkitFilter: "blur(9px)" }}
             data-testid="report-blur-preview">
          {/* Salary tiles preview */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">{t("salary_sec")}</div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {(tiles.length > 0 ? tiles : [{key:"a"},{key:"b"},{key:"c"},{key:"d"}]).map((s, i) => (
                <div key={s.key || i} className="rounded-xl border border-border p-4 flex flex-col items-center min-h-[130px]">
                  <div className="text-[10px] uppercase tracking-widest text-accent">{lang === "de" ? (s.label_de || "•••") : (s.label_en || "•••")}</div>
                  <div className="mt-3 text-2xl font-semibold">{s.salary_max ? `${(s.salary_max).toLocaleString("de-DE")} €` : (s.display_text || "•••")}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{lang === "de" ? (s.exp_label_de || "•••") : (s.exp_label_en || "•••")}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Skill analysis preview */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Skills</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[["emerald", t("strong"), sa.already_strong], ["amber", t("partial"), sa.partially_covered], ["rose", t("gap"), sa.gap]].map(([tone, title, items], i) => (
                <div key={i} className="rounded-xl border border-border p-3">
                  <div className="text-xs font-semibold">{title}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(items || []).slice(0, 6).map((s, k) => (
                      <span key={k} className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-xs">{s.canonical_skill || s}</span>
                    ))}
                    {(!items || items.length === 0) && <span className="text-xs text-muted-foreground">•••</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap preview */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">{t("roadmap_sec")}</div>
            <div className="mt-3 space-y-2">
              {(roadmap.length > 0 ? roadmap : [{weeks:"1-2"},{weeks:"3-4"},{weeks:"5-6"},{weeks:"7-8"}]).slice(0, 4).map((p, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-accent w-16">{lang === "de" ? "Woche" : "Week"} {p.weeks}</div>
                  <div className="flex-1 text-sm">{p.title_de || p.title_en || "•••••••••••••••••••"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Prep courses preview */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">{t("prep_sec")}</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(prep.length > 0 ? prep : [{}, {}]).slice(0, 4).map((c, i) => (
                <div key={i} className="rounded-xl border border-border p-3">
                  <div className="text-sm font-semibold">{c.title || "••••••••••••••••••"}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{c.duration_min ? `${Math.round(c.duration_min)} min` : "••• min"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Centered email form overlay — anchored to top so it stays close to the header on all viewports */}
        <div className="absolute inset-x-0 top-0 z-10 flex justify-center px-4 pt-2 md:pt-4">
          <form onSubmit={onSubmit} className="w-full max-w-lg rounded-2xl border border-primary/40 bg-card/95 backdrop-blur-xl shadow-2xl p-5 md:p-7" data-testid="email-gate-form">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-accent">
              <Sparkles className="h-3 w-3"/> {lang === "de" ? "Fast fertig" : "Almost there"}
            </div>
            <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {t("unlock_title")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("unlock_desc")}</p>
            <div className="mt-5 flex gap-2">
              <Input data-testid="email-input" type="email" required placeholder={t("placeholder")} value={email} onChange={e => setEmail(e.target.value)} className="flex-1 h-11"/>
              <Button data-testid="email-submit" type="submit" disabled={loading} className="h-11 rounded-full px-5 font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <>{t("open")} <ArrowRight className="h-4 w-4 ml-2"/></>}
              </Button>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">{t("free_line")}</div>
            <label className="mt-4 flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="marketing-opt-in-label">
              <input type="checkbox" data-testid="marketing-opt-in"
                     checked={!!marketingOptIn}
                     onChange={e => setMarketingOptIn(e.target.checked)}
                     className="mt-0.5 accent-primary" />
              <span>{lang === "de"
                ? "Ja, ich möchte gelegentlich Karriere-Tipps und Kurs-Updates von ERP Institute per E-Mail erhalten. Abbestellung jederzeit möglich."
                : "Yes, I'd like to occasionally receive career tips and course updates from ERP Institute by email. Unsubscribe anytime."}
              </span>
            </label>
          </form>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
      <div data-testid={testid} className="mt-1 text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{value}</div>
    </div>
  );
}

function Chip({ children, active, onClick, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"}`}>
      {children}
    </button>
  );
}
